import React from "react";
import "./ProfileSections.css";

// Import option arrays for label mapping
import SchoolBoard from "../options/schoolBoard";
import Streams from "../options/Streams";
import FutureDegree from "../options/FutureDegree";
import CurrentClass8th from "../options/CurrentClass8th";
import CurrentClass910 from "../options/CurrentClass910";
import CurrentClass1112 from "../options/CurrentClass1011";
import Opportunities from "../options/OpportunitiesTypes8th";
import Olympiads from "../options/Olympiads";
import CollegeList from "../userForms/FormsData/Colleges";
import FutureFields from "../userForms/FormsData/FutureFields";

const ProfileEducationSection = ({
  educationData,
  categoryType,
  displayLabelValue,
}) => {
  // Function to get label from options based on value
  const getLabelFromOptions = (value, options) => {
    if (!value) return "Not provided";

    console.log(`Getting label for value: ${value}, type: ${typeof value}`);

    // Direct string mapping for SchoolBoard
    if (options === SchoolBoard) {
      if (value === "1") return "CBSE";
      if (value === "2") return "ICSE";
      if (value === "3") return "State Board";
    }

    // For other option types, simple lookup with string comparison
    if (options && Array.isArray(options)) {
      const option = options.find(
        (item) => String(item.value) === String(value)
      );
      if (option) return option.label;

      // Try direct index lookup (1-based)
      const index = parseInt(value) - 1;
      if (!isNaN(index) && index >= 0 && index < options.length) {
        return options[index].label;
      }
    }

    // Return the original value if no mapping is found
    return String(value);
  };

  // Function to parse JSON arrays safely
  const parseJsonArray = (jsonString, options) => {
    if (!jsonString) return [];

    // Use the displayLabelValue function if provided
    if (displayLabelValue && Array.isArray(jsonString)) {
      return [displayLabelValue(jsonString)];
    }

    try {
      // If it's already an array object, use it directly
      if (Array.isArray(jsonString)) {
        return jsonString
          .map((value) => {
            // Handle objects with label/value directly
            if (typeof value === "object" && value !== null) {
              if (value.label) return value.label;
              if (value.value) {
                const option = options.find(
                  (opt) => opt.value === value.value.toString()
                );
                return option ? option.label : String(value.value);
              }
            }

            const valueStr = String(value);
            const option = options.find((opt) => opt.value === valueStr);
            return option ? option.label : valueStr;
          })
          .filter(Boolean);
      }

      // If it's a string, try to parse it
      if (typeof jsonString === "string") {
        const values = JSON.parse(jsonString);
        if (!Array.isArray(values)) return [];

        return values
          .map((value) => {
            // Handle case where value might be an object
            if (typeof value === "object" && value !== null) {
              if (value.label) return value.label;
              if (value.value) value = value.value;
            }

            const valueStr = String(value);
            const option = options.find((opt) => opt.value === valueStr);
            return option ? option.label : valueStr;
          })
          .filter(Boolean);
      }

      return [];
    } catch (error) {
      // If we can't parse it but it's an object with certain properties, try to handle it
      if (typeof jsonString === "object" && jsonString !== null) {
        if (jsonString.label) return [jsonString.label];
      }
      return [];
    }
  };

  // Function to parse interested regions
  const parseRegions = (regionsData) => {
    if (!regionsData) return [];

    try {
      // If it's already an object
      if (typeof regionsData === "object" && !Array.isArray(regionsData)) {
        return formatRegionObject(regionsData);
      }

      // If it's a string, parse it
      if (typeof regionsData === "string") {
        const parsed = JSON.parse(regionsData);

        if (Array.isArray(parsed)) {
          return parsed
            .flatMap((region) => {
              return formatRegionObject(region);
            })
            .filter(Boolean);
        }

        return formatRegionObject(parsed);
      }

      // If it's already an array
      if (Array.isArray(regionsData)) {
        return regionsData
          .flatMap((region) => {
            return formatRegionObject(region);
          })
          .filter(Boolean);
      }

      return [];
    } catch (error) {
      return [];
    }
  };

  // Helper function to format region object
  const formatRegionObject = (region) => {
    if (!region) return [];

    const results = [];

    // If region has a name, add it
    if (region.regionName) {
      results.push(region.regionName);
    }

    // If region has subRegions, add them
    if (region.subRegions && Array.isArray(region.subRegions)) {
      region.subRegions.forEach((subRegion) => {
        if (subRegion.label && subRegion.check) {
          results.push(subRegion.label);
        }
      });
    }

    return results;
  };

  // Function to parse exams data
  const parseExams = (examsData) => {
    if (!examsData) return [];

    try {
      // If it's already an array
      if (Array.isArray(examsData)) {
        return examsData.map((exam) => formatExam(exam));
      }

      // If it's a string, parse it
      if (typeof examsData === "string") {
        const parsed = JSON.parse(examsData);

        if (Array.isArray(parsed)) {
          return parsed.map((exam) => formatExam(exam));
        }

        return [formatExam(parsed)];
      }

      // If it's an object
      if (typeof examsData === "object") {
        return [formatExam(examsData)];
      }

      return [];
    } catch (error) {
      return [];
    }
  };

  // Helper function to format exam data
  const formatExam = (exam) => {
    if (!exam) return null;

    const name = exam.name || "";
    const percentage = exam.percentage || "";
    const date = exam.date || "";
    const criteria = exam.criteria || "";

    return {
      name,
      percentage,
      date,
      criteria,
    };
  };

  // Helper function to format exam score based on exam type
  const formatExamScore = (exam) => {
    if (!exam.percentage) return "";

    // Handle different exam types
    if (exam.name && exam.name.toLowerCase().includes("ielts")) {
      return `Score: ${exam.percentage}/9`;
    } else if (exam.name && exam.name.toLowerCase().includes("gre")) {
      return `Score: ${exam.percentage}/340`;
    } else if (exam.name && exam.name.toLowerCase().includes("gmat")) {
      return `Score: ${exam.percentage}/800`;
    } else if (exam.name && exam.name.toLowerCase().includes("toefl")) {
      return `Score: ${exam.percentage}/120`;
    } else if (exam.name && exam.name.toLowerCase().includes("sat")) {
      return `Score: ${exam.percentage}/1600`;
    } else if (exam.name && exam.name.toLowerCase().includes("act")) {
      return `Score: ${exam.percentage}/36`;
    } else {
      // For other exams, use the criteria if available
      return `Score: ${exam.percentage}${
        exam.criteria ? ` (${exam.criteria})` : ""
      }`;
    }
  };

  // Render education section based on category
  const renderEducationContent = () => {
    if (!educationData) {
      return <p>No educational data available</p>;
    }

    // Add category as a fallback from educationData if needed
    const effectiveCategoryType =
      categoryType || educationData.categoryType || "Unknown";

    try {
      switch (effectiveCategoryType.toLowerCase()) {
        case "below8th":
          return renderBelow8thEducation();
        case "9-10th":
          return renderNinthTenthEducation();
        case "11-12th":
          return renderElevenTwelfthEducation();
        case "bachelors":
          return renderBachelorsEducation();
        case "masters":
          return renderMastersEducation();
        case "phd":
          return renderPhdEducation();
        default:
          return <p>Unknown education category: {effectiveCategoryType}</p>;
      }
    } catch (error) {
      return <p>Error displaying educational information</p>;
    }
  };

  // Below 8th grade education section
  const renderBelow8thEducation = () => {
    try {
      const currentClass = getLabelFromOptions(
        educationData.currentclass,
        CurrentClass8th
      );
      const schoolBoard = getLabelFromOptions(
        educationData.schoolBoard,
        SchoolBoard
      );

      // Handle opportunities - check if it's already an array or needs parsing
      let interestedFields = [];
      if (Array.isArray(educationData.opportunities)) {
        interestedFields = educationData.opportunities.map((item) =>
          typeof item === "object" ? item.label || String(item) : String(item)
        );
      } else {
        interestedFields = parseJsonArray(
          educationData.opportunities,
          Opportunities
        );
      }

      // Handle olympiads - check if it's already an array or needs parsing
      let olympiadsParticipated = [];
      if (Array.isArray(educationData.olympiads)) {
        olympiadsParticipated = educationData.olympiads.map((item) =>
          typeof item === "object" ? item.label || String(item) : String(item)
        );
      } else {
        olympiadsParticipated = parseJsonArray(
          educationData.olympiads,
          Olympiads
        );
      }

      return (
        <div className="user-education-content">
          <div className="user-education-section">
            <h3>School Information</h3>
            <div className="user-profile-info-grid">
              <div className="user-profile-info-item">
                <label>Current Class</label>
                <span>{currentClass}</span>
              </div>
              <div className="user-profile-info-item">
                <label>School Name</label>
                <span>{educationData.schoolname || "Not provided"}</span>
              </div>
              <div className="user-profile-info-item">
                <label>School Board</label>
                <span>{schoolBoard}</span>
              </div>
            </div>
          </div>

          {interestedFields.length > 0 && (
            <div className="user-education-section">
              <h3>Interests</h3>
              <div className="user-tags-container">
                {interestedFields.map((field, idx) => (
                  <span key={idx} className="user-tag">
                    {field}
                  </span>
                ))}
              </div>
            </div>
          )}

          {olympiadsParticipated.length > 0 && (
            <div className="user-education-section">
              <h3>Olympiads Participated</h3>
              <div className="user-tags-container">
                {olympiadsParticipated.map((olympiad, idx) => (
                  <span key={idx} className="user-tag">
                    {olympiad}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    } catch (error) {
      return <p>Error displaying Below 8th education information</p>;
    }
  };

  // 9-10th grade education section
  const renderNinthTenthEducation = () => {
    const currentClass = getLabelFromOptions(
      educationData.currentclass,
      CurrentClass910
    );

    // Use consistent approach for schoolBoard
    const schoolBoard = getLabelFromOptions(
      educationData.schoolBoard,
      SchoolBoard
    );

    const futureDegreeValue = getLabelFromOptions(
      educationData.futuredegree,
      FutureDegree
    );

    // Restore the missing interestedFields variable
    const interestedFields = parseJsonArray(
      educationData.opportunities,
      Opportunities
    );

    const olympiadsParticipated = parseJsonArray(
      educationData.olympiadparticipated,
      Olympiads
    );
    const olympiadsInterested = parseJsonArray(
      educationData.olympiadinterested,
      Olympiads
    );

    return (
      <div className="user-education-content">
        <div className="user-education-section">
          <h3>School Information</h3>
          <div className="user-profile-info-grid">
            <div className="user-profile-info-item">
              <label>Current Class</label>
              <span>{currentClass}</span>
            </div>
            <div className="user-profile-info-item">
              <label>School Name</label>
              <span>{educationData.schoolname || "Not provided"}</span>
            </div>
            <div className="user-profile-info-item">
              <label>School Board</label>
              <span>{schoolBoard}</span>
            </div>
          </div>
        </div>

        {interestedFields.length > 0 && (
          <div className="user-education-section">
            <h3>Interests</h3>
            <div className="user-tags-container">
              {interestedFields.map((field, idx) => (
                <span key={idx} className="user-tag">
                  {field}
                </span>
              ))}
            </div>
          </div>
        )}

        {olympiadsParticipated.length > 0 && (
          <div className="user-education-section">
            <h3>Olympiads Participated</h3>
            <div className="user-tags-container">
              {olympiadsParticipated.map((olympiad, idx) => (
                <span key={idx} className="user-tag">
                  {olympiad}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // 11-12th grade education section
  const renderElevenTwelfthEducation = () => {
    const currentClass = getLabelFromOptions(
      educationData.currentclass,
      CurrentClass1112
    );
    const schoolBoard = getLabelFromOptions(
      educationData.schoolBoard,
      SchoolBoard
    );

    // Use consistent approach for tenthBoard
    const tenthBoard = getLabelFromOptions(
      educationData.class10thboard,
      SchoolBoard
    );

    const currentStream = getLabelFromOptions(
      educationData.currentstream,
      Streams
    );
    const futureDegreeValue = getLabelFromOptions(
      educationData.futuredegree,
      FutureDegree
    );

    const olympiadsParticipated = parseJsonArray(
      educationData.olympiadparticipated,
      Olympiads
    );
    const olympiadsInterested = parseJsonArray(
      educationData.olympiadinterested,
      Olympiads
    );

    return (
      <div className="user-education-content">
        <div className="user-education-section">
          <h3>Current Education</h3>
          <div className="user-profile-info-grid">
            <div className="user-profile-info-item">
              <label>Current Class</label>
              <span>{currentClass}</span>
            </div>
            <div className="user-profile-info-item">
              <label>Current Stream</label>
              <span>{currentStream}</span>
            </div>
            <div className="user-profile-info-item">
              <label>School Name</label>
              <span>{educationData.schoolname || "Not provided"}</span>
            </div>
            <div className="user-profile-info-item">
              <label>School Board</label>
              <span>{schoolBoard}</span>
            </div>
          </div>
        </div>

        <div className="user-education-section">
          <h3>Previous Education</h3>
          <div className="user-profile-info-grid">
            <div className="user-profile-info-item">
              <label>10th School</label>
              <span>{educationData.tenthschoolname || "Not provided"}</span>
            </div>
            <div className="user-profile-info-item">
              <label>10th Board</label>
              <span>{tenthBoard}</span>
            </div>
            <div className="user-profile-info-item">
              <label>10th Percentage</label>
              <span>{educationData.class10percent || "Not provided"}</span>
            </div>
          </div>
        </div>

        <div className="user-education-section">
          <h3>Future Plans</h3>
          <div className="user-profile-info-grid">
            <div className="user-profile-info-item">
              <label>Future Degree</label>
              <span>{futureDegreeValue}</span>
            </div>
            <div className="user-profile-info-item">
              <label>Budget</label>
              <span>{educationData.budget || "Not specified"}</span>
            </div>
          </div>
        </div>

        {(olympiadsParticipated.length > 0 ||
          olympiadsInterested.length > 0) && (
          <div className="user-education-section">
            <h3>Olympiads</h3>

            {olympiadsParticipated.length > 0 && (
              <div className="user-profile-subsection">
                <h4>Participated</h4>
                <div className="user-tags-container">
                  {olympiadsParticipated.map((olympiad, idx) => (
                    <span key={idx} className="user-tag">
                      {olympiad}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {olympiadsInterested.length > 0 && (
              <div className="user-profile-subsection">
                <h4>Interested</h4>
                <div className="user-tags-container">
                  {olympiadsInterested.map((olympiad, idx) => (
                    <span key={idx} className="user-tag">
                      {olympiad}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Bachelors education section
  const renderBachelorsEducation = () => {
    console.log("Bachelor Education Data:", {
      collegename: educationData.collegename,
      collegeyear: educationData.collegeyear,
      tenthBoard: educationData.tenthBoard,
      twelthBoard: educationData.twelthBoard,
    });

    let collegeName = "Not provided";
    try {
      if (educationData.collegename) {
        // Handle both direct values and objects
        if (
          typeof educationData.collegename === "object" &&
          educationData.collegename.label
        ) {
          collegeName = educationData.collegename.label;
        } else {
          // Try numeric conversion first
          try {
            const collegeIndex = parseInt(educationData.collegename) - 1;
            if (
              !isNaN(collegeIndex) &&
              collegeIndex >= 0 &&
              collegeIndex < CollegeList.length
            ) {
              collegeName = CollegeList[collegeIndex]?.label;
            } else {
              // If not a valid index, use the raw value
              collegeName = String(educationData.collegename);
            }
          } catch (err) {
            // Fallback to the raw value
            collegeName = String(educationData.collegename);
          }
        }
      }
    } catch (e) {
      console.error("Error processing college name:", e);
      collegeName = String(educationData.collegename || "Not provided");
    }

    // Get the college year, ensuring it's a string
    let collegeYear = "Not provided";
    if (educationData.collegeyear) {
      // Direct mapping for college year
      if (educationData.collegeyear === "1") {
        collegeYear = "1st Year";
      } else if (educationData.collegeyear === "2") {
        collegeYear = "2nd Year";
      } else if (educationData.collegeyear === "3") {
        collegeYear = "3rd Year";
      } else if (educationData.collegeyear === "4") {
        collegeYear = "4th Year";
      } else if (educationData.collegeyear === "5") {
        collegeYear = "5th Year";
      } else {
        collegeYear = String(educationData.collegeyear);
      }
    }

    // Fix board labels to use displayLabelValue or find the correct label
    let tenthBoard = "Not provided";
    let twelthBoard = "Not provided";

    try {
      // Log board values for debugging
      console.log("Board values:", {
        tenthBoardValue: educationData.tenthBoard,
        tenthBoardType: typeof educationData.tenthBoard,
        twelthBoardValue: educationData.twelthBoard,
        twelthBoardType: typeof educationData.twelthBoard,
      });

      // Use getLabelFromOptions for consistent handling across all board values
      tenthBoard = getLabelFromOptions(educationData.tenthBoard, SchoolBoard);
      twelthBoard = getLabelFromOptions(educationData.twelthBoard, SchoolBoard);

      console.log("Resolved board labels:", { tenthBoard, twelthBoard });
    } catch (e) {
      console.error("Board resolution error:", e);
    }

    // Parse future fields
    let futureFields = [];
    try {
      futureFields = parseJsonArray(educationData.futurefields, FutureFields);
    } catch (e) {
      // Silent error handling
    }

    // Parse interested regions
    let interestedRegions = [];
    try {
      interestedRegions = parseRegions(educationData.interestedregions);
    } catch (e) {
      // Silent error handling
    }

    // Parse exams
    let exams = [];
    try {
      exams = parseExams(educationData.exams);
    } catch (e) {
      // Silent error handling
    }

    return (
      <div className="user-education-content">
        <div className="user-education-section">
          <h3>Current Education</h3>
          <div className="user-profile-info-grid">
            <div className="user-profile-info-item">
              <label>College</label>
              <span>{collegeName}</span>
            </div>
            <div className="user-profile-info-item">
              <label>Year</label>
              <span>{collegeYear}</span>
            </div>
          </div>
        </div>

        <div className="user-education-section">
          <h3>Previous Education</h3>
          <div className="user-profile-info-grid">
            <div className="user-profile-info-item">
              <label>12th School</label>
              <span>{educationData.twelthschoolname || "Not provided"}</span>
            </div>
            <div className="user-profile-info-item">
              <label>12th Board</label>
              <span>{twelthBoard}</span>
            </div>
            <div className="user-profile-info-item">
              <label>12th Percentage</label>
              <span>{educationData.twelthPercentage || "Not provided"}</span>
            </div>
            <div className="user-profile-info-item">
              <label>10th School</label>
              <span>{educationData.tenthschoolname || "Not provided"}</span>
            </div>
            <div className="user-profile-info-item">
              <label>10th Board</label>
              <span>{tenthBoard}</span>
            </div>
            <div className="user-profile-info-item">
              <label>10th Percentage</label>
              <span>{educationData.tenthPercentage || "Not provided"}</span>
            </div>
          </div>
        </div>

        {exams.length > 0 && (
          <div className="user-education-section">
            <h3>Exams</h3>
            <div className="user-profile-info-grid">
              {exams.map((exam, idx) => (
                <div
                  key={idx}
                  className="user-profile-info-item user-exam-item"
                >
                  <label>{exam.name}</label>
                  <span>
                    {exam.percentage ? formatExamScore(exam) : ""}
                    {!exam.percentage && exam.criteria
                      ? `(${exam.criteria})`
                      : ""}
                    {exam.date
                      ? `${exam.percentage || exam.criteria ? ", " : ""}Date: ${
                          exam.date
                        }`
                      : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {futureFields.length > 0 && (
          <div className="user-education-section">
            <h3>Future Interests</h3>
            <div className="user-tags-container">
              {futureFields.map((field, idx) => (
                <span key={idx} className="user-tag">
                  {field}
                </span>
              ))}
            </div>
          </div>
        )}

        {interestedRegions.length > 0 && (
          <div className="user-education-section">
            <h3>Interested Regions</h3>
            <div className="user-tags-container">
              {interestedRegions.map((region, idx) => (
                <span key={idx} className="user-tag">
                  {region}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Masters education section
  const renderMastersEducation = () => {
    console.log("Masters Education Data:", {
      collegename: educationData.collegename,
      collegeyear: educationData.collegeyear,
      tenthBoard: educationData.tenthBoard,
      twelthBoard: educationData.twelthBoard,
    });

    let collegeName = "Not provided";
    try {
      if (educationData.collegename) {
        if (
          typeof educationData.collegename === "object" &&
          educationData.collegename.label
        ) {
          collegeName = educationData.collegename.label;
        } else {
          // Try numeric conversion first
          try {
            const collegeIndex = parseInt(educationData.collegename) - 1;
            if (
              !isNaN(collegeIndex) &&
              collegeIndex >= 0 &&
              collegeIndex < CollegeList.length
            ) {
              collegeName = CollegeList[collegeIndex]?.label;
            } else {
              // If not a valid index, use the raw value
              collegeName = String(educationData.collegename);
            }
          } catch (err) {
            // Fallback to the raw value
            collegeName = String(educationData.collegename);
          }
        }
      }
    } catch (e) {
      console.error("Error processing college name:", e);
      collegeName = String(educationData.collegename || "Not provided");
    }

    // Get the college year, ensuring it's a string
    let collegeYear = "Not provided";
    if (educationData.collegeyear) {
      // Direct mapping for college year
      if (educationData.collegeyear === "1") {
        collegeYear = "1st Year";
      } else if (educationData.collegeyear === "2") {
        collegeYear = "2nd Year";
      } else if (educationData.collegeyear === "3") {
        collegeYear = "3rd Year";
      } else if (educationData.collegeyear === "4") {
        collegeYear = "4th Year";
      } else if (educationData.collegeyear === "5") {
        collegeYear = "5th Year";
      } else {
        collegeYear = String(educationData.collegeyear);
      }
    }

    // Fix board labels to use displayLabelValue or find the correct label
    let tenthBoard = "Not provided";
    let twelthBoard = "Not provided";

    try {
      // Log board values for debugging
      console.log("Board values:", {
        tenthBoardValue: educationData.tenthBoard,
        tenthBoardType: typeof educationData.tenthBoard,
        twelthBoardValue: educationData.twelthBoard,
        twelthBoardType: typeof educationData.twelthBoard,
      });

      // Use getLabelFromOptions for consistent handling across all board values
      tenthBoard = getLabelFromOptions(educationData.tenthBoard, SchoolBoard);
      twelthBoard = getLabelFromOptions(educationData.twelthBoard, SchoolBoard);

      console.log("Resolved board labels:", { tenthBoard, twelthBoard });
    } catch (e) {
      console.error("Board resolution error:", e);
    }

    // Parse future fields
    const futureFields = parseJsonArray(
      educationData.futureOpportunityTypes,
      FutureFields
    );

    // Parse interested regions
    let interestedRegions = [];
    try {
      interestedRegions = parseRegions(educationData.interestedregions);
    } catch (e) {
      // Silent error handling
    }

    // Parse exams
    let exams = [];
    try {
      exams = parseExams(educationData.exams);
    } catch (e) {
      // Silent error handling
    }

    return (
      <div className="user-education-content">
        <div className="user-education-section">
          <h3>Current Education</h3>
          <div className="user-profile-info-grid">
            <div className="user-profile-info-item">
              <label>College</label>
              <span>{collegeName}</span>
            </div>
            <div className="user-profile-info-item">
              <label>Year</label>
              <span>{collegeYear}</span>
            </div>
            <div className="user-profile-info-item">
              <label>Degree</label>
              <span>{educationData.currentdegree || "Not provided"}</span>
            </div>
            <div className="user-profile-info-item">
              <label>GPA</label>
              <span>{educationData.gpa || "Not provided"}</span>
            </div>
          </div>
        </div>

        <div className="user-education-section">
          <h3>Previous Education</h3>
          <div className="user-profile-info-grid">
            <div className="user-profile-info-item">
              <label>12th School</label>
              <span>{educationData.twelthschoolname || "Not provided"}</span>
            </div>
            <div className="user-profile-info-item">
              <label>12th Board</label>
              <span>{twelthBoard}</span>
            </div>
            <div className="user-profile-info-item">
              <label>12th Percentage</label>
              <span>{educationData.twelthPercentage || "Not provided"}</span>
            </div>
            <div className="user-profile-info-item">
              <label>10th School</label>
              <span>{educationData.tenthschoolname || "Not provided"}</span>
            </div>
            <div className="user-profile-info-item">
              <label>10th Board</label>
              <span>{tenthBoard}</span>
            </div>
            <div className="user-profile-info-item">
              <label>10th Percentage</label>
              <span>{educationData.tenthPercentage || "Not provided"}</span>
            </div>
          </div>
        </div>

        {exams.length > 0 && (
          <div className="user-education-section">
            <h3>Exams</h3>
            <div className="user-profile-info-grid">
              {exams.map((exam, idx) => (
                <div
                  key={idx}
                  className="user-profile-info-item user-exam-item"
                >
                  <label>{exam.name}</label>
                  <span>
                    {exam.percentage ? formatExamScore(exam) : ""}
                    {!exam.percentage && exam.criteria
                      ? `(${exam.criteria})`
                      : ""}
                    {exam.date
                      ? `${exam.percentage || exam.criteria ? ", " : ""}Date: ${
                          exam.date
                        }`
                      : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {futureFields.length > 0 && (
          <div className="user-education-section">
            <h3>Future Interests</h3>
            <div className="user-tags-container">
              {futureFields.map((field, idx) => (
                <span key={idx} className="user-tag">
                  {field}
                </span>
              ))}
            </div>
          </div>
        )}

        {interestedRegions.length > 0 && (
          <div className="user-education-section">
            <h3>Interested Regions</h3>
            <div className="user-tags-container">
              {interestedRegions.map((region, idx) => (
                <span key={idx} className="user-tag">
                  {region}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // PhD education section
  const renderPhdEducation = () => {
    let collegeName = "Not provided";
    try {
      if (educationData.collegename) {
        // Handle both direct values and objects
        if (
          typeof educationData.collegename === "object" &&
          educationData.collegename.label
        ) {
          collegeName = educationData.collegename.label;
        } else {
          // Try numeric conversion first
          try {
            const collegeIndex = parseInt(educationData.collegename) - 1;
            if (
              !isNaN(collegeIndex) &&
              collegeIndex >= 0 &&
              collegeIndex < CollegeList.length
            ) {
              collegeName = CollegeList[collegeIndex]?.label;
            } else {
              // If not a valid index, use the raw value
              collegeName = String(educationData.collegename);
            }
          } catch (err) {
            // Fallback to the raw value
            collegeName = String(educationData.collegename);
          }
        }
      }
    } catch (e) {
      console.error("Error processing college name:", e);
      collegeName = String(educationData.collegename || "Not provided");
    }

    // Get the college year, ensuring it's a string
    let collegeYear = "Not provided";
    if (educationData.collegeyear) {
      // Direct mapping for college year
      if (educationData.collegeyear === "1") {
        collegeYear = "1st Year";
      } else if (educationData.collegeyear === "2") {
        collegeYear = "2nd Year";
      } else if (educationData.collegeyear === "3") {
        collegeYear = "3rd Year";
      } else if (educationData.collegeyear === "4") {
        collegeYear = "4th Year";
      } else if (educationData.collegeyear === "5") {
        collegeYear = "5th Year";
      } else {
        collegeYear = String(educationData.collegeyear);
      }
    }

    const futureFields = parseJsonArray(
      educationData.futureOpportunityTypes,
      FutureFields
    );

    return (
      <div className="user-education-content">
        <div className="user-education-section">
          <h3>Current Education</h3>
          <div className="user-profile-info-grid">
            <div className="user-profile-info-item">
              <label>College</label>
              <span>{collegeName}</span>
            </div>
            <div className="user-profile-info-item">
              <label>Year</label>
              <span>{collegeYear}</span>
            </div>
            <div className="user-profile-info-item">
              <label>Specialization</label>
              <span>{educationData.specialization || "Not provided"}</span>
            </div>
            <div className="user-profile-info-item">
              <label>GPA</label>
              <span>{educationData.gpa || "Not provided"}</span>
            </div>
            <div className="user-profile-info-item">
              <label>Expected Compensation</label>
              <span>{educationData.compensation || "Not provided"}</span>
            </div>
          </div>
        </div>

        {futureFields.length > 0 && (
          <div className="user-education-section">
            <h3>Research Interests</h3>
            <div className="user-tags-container">
              {futureFields.map((field, idx) => (
                <span key={idx} className="user-tag">
                  {field}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="user-profile-card">
      <div className="user-profile-card-header">
        <h2>Educational Information</h2>
      </div>

      <div className="user-profile-card-content">
        {renderEducationContent()}
      </div>
    </div>
  );
};

export default ProfileEducationSection;
